import type { ProRenderFieldPropsType } from '@ant-design/pro-provider'
import type { SimpleRelationValidPeriod } from '@app/icare/entities/patient/schema/PatientRelation'
import { t } from '@lingui/core/macro'
import { formatDate } from '@pkg/shared/utils'
import { Typography } from 'antd'
import dayjs from 'dayjs'

type RangeStatus = 'active' | 'expired' | 'pending' | 'unbefristet'

const statusTextType: Record<RangeStatus, 'success' | 'danger' | 'warning' | undefined> = {
  active: 'success',
  unbefristet: 'success',
  expired: 'danger',
  pending: 'warning',
}

function renderActiveRange(value: SimpleRelationValidPeriod): { label: string; status: RangeStatus } {
  const today = dayjs()
  const from = value.activeFrom || null
  const to = value.activeTo || null

  if (!from && !to) {
    return { label: t`Unbefristet`, status: 'unbefristet' }
  }

  if (!from && to) {
    return today.isBefore(dayjs(to))
      ? { label: t`Gültig bis ${formatDate(to)}`, status: 'active' }
      : { label: t`Nicht mehr gültig seit ${formatDate(to)}`, status: 'expired' }
  }

  if (from && !to) {
    return dayjs(from).isAfter(today)
      ? { label: t`Gültig ab ${formatDate(from)}`, status: 'pending' }
      : { label: t`Gültig seit ${formatDate(from)}`, status: 'active' }
  }

  // both present — previous branches exhaust all null combinations
  if (from !== null && to !== null) {
    if (today.isBefore(dayjs(from))) {
      return { label: t`Gültig ab ${formatDate(from)}`, status: 'pending' }
    }
    if (today.isAfter(dayjs(to))) {
      return { label: t`Nicht mehr gültig seit ${formatDate(to)}`, status: 'expired' }
    }
    return { label: t`Gültig seit ${formatDate(from)}`, status: 'active' }
  }
  return { label: t`Unbefristet`, status: 'unbefristet' }
}

export const getActiveRangeStatus = (val: SimpleRelationValidPeriod) => {
  const { label, status } = renderActiveRange(val)

  return { label, status, color: statusTextType[status] }
}

export const activeRangeValueType = {
  render: (text: SimpleRelationValidPeriod | null | undefined, opts: { plain?: boolean } = {}) => {
    if (!text) return '-'

    const { label, color } = getActiveRangeStatus(text)

    return <Typography.Text type={opts.plain ? undefined : color}>{label}</Typography.Text>
  },
  renderFormItem: text => text,
} satisfies ProRenderFieldPropsType
